import React, { useEffect } from 'react';
import { Popover as HLPopover } from '@headlessui/react';
import PopoverButton from './PopoverButton';
import PopoverSpan from './PopoverSpan';
import PopoverPanel from './PopoverPanel';
import { PopoverContext } from './usePopoverContext';
import {
  autoUpdate,
  flip,
  offset,
  Placement,
  shift,
  useFloating,
} from '@floating-ui/react-dom';
import tw from 'twin.macro';
import clsx from 'clsx';

interface Props {
  children: any;
  portal?: boolean;
  large?: boolean;
  fill?: boolean;
  hover?: boolean;
  flex?: boolean;
  onClickOutside?: (event: any) => void;
  fullHeight?: boolean;
  offsetInPx?: number;
  placement?: Placement;
  onClose?: () => void;
}

export function PopoverContent(
  props: Props & {
    open: boolean;
  },
) {
  let timeout: ReturnType<typeof setTimeout>;
  const timeoutDuration = 10;
  const {
    fill = false,
    portal = false,
    children,
    large = false,
    hover = false,
    placement = 'bottom',
    onClickOutside,
    fullHeight = false,
    offsetInPx = 2,
    open,
  } = props;
  const { refs, floatingStyles } = useFloating<HTMLElement>({
    middleware: [offset(offsetInPx), flip(), shift()],
    placement,
    whileElementsMounted: autoUpdate,
  });

  const toggleMenu = () => {
    if (props.onClose && open) {
      props.onClose();
    }
    if (open !== undefined) {
      if (refs.reference.current) {
        // We use that in PopoverButton to know if the click is programmatic
        // meaning it's probably a hover
        // Or if it comes from the user clicking on the button
        if (refs.reference.current?.dataset) {
          refs.reference.current.dataset.triggerType = 'programmatic';
        }
        (refs.reference.current as any)?.click();
      }
    }
  };

  // We'll expose this so children can close the popover
  const closePopover = () => {
    toggleMenu();
  };

  const onHover = (action: 'onMouseEnter' | 'onMouseLeave') => {
    if (
      (!open && action === 'onMouseEnter') ||
      (open && action === 'onMouseLeave')
    ) {
      clearTimeout(timeout);
      timeout = setTimeout(() => toggleMenu(), timeoutDuration);
    }
    // else: do nothing
  };

  const handleClickOutside = event => {
    const portalRoot = document.getElementById('headlessui-portal-root');
    if (
      refs.floating &&
      refs.floating.current &&
      !portalRoot?.contains(event.target) // if the element clicked is in a portal, do not consider it as outside
    ) {
      if (!refs.floating.current.contains(event.target)) {
        if (onClickOutside) {
          // Attention ici on shortcut le toggleMenu, et c'est une feature (cf EditCustomField)
          return onClickOutside(event);
        }
        toggleMenu();
      }
    }
  };

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return;
  }, [open]);

  return (
    <div
      onMouseEnter={() => hover && onHover('onMouseEnter')}
      onMouseLeave={() => hover && onHover('onMouseLeave')}
      css={[
        tw`relative text-left`,
        !fill && tw`inline-block`,
        fullHeight && tw`h-full`,
      ]}
    >
      <PopoverContext.Provider
        value={{
          fill,
          large,
          open,
          refs,
          floatingStyles,
          portal,
          close: closePopover, // <-- Provide a close method
        }}
      >
        {children}
      </PopoverContext.Provider>
    </div>
  );
}

export default function Popover(
  props: Props & {
    as?: string;
    className?: string;
    open?: boolean;
  },
) {
  return (
    <HLPopover
      css={[
        !props.fill && !props.flex && tw`inline-block`,
        props.flex && tw`flex`,
      ]}
      className={clsx(props.className, 'tsh-menu')}
    >
      {({ open }) => (
        <PopoverContent
          {...props}
          open={props.open === undefined ? open : props.open}
        />
      )}
    </HLPopover>
  );
}

Popover.Button = PopoverButton;
Popover.Span = PopoverSpan;
Popover.Panel = PopoverPanel;
